<template>
  <div class="min-h-screen h-screen flex overflow-hidden">
    <!-- Left Panel - Form -->
    <div class="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-20 bg-white dark:bg-corporate-950">
      <div class="w-full max-w-md mx-auto">
        <!-- Header with Logo -->
        <div class="flex items-center justify-center mb-10">
          <router-link to="/" class="flex items-center justify-center">
            <img 
              src="/images/logo-icon.png" 
              alt="Dashboard" 
              class="h-12 w-auto"
            />
          </router-link>
        </div>

        <!-- Title -->
        <div class="mb-8 text-center">
          <h1 class="text-2xl font-semibold text-corporate-900 dark:text-white mb-2">
            {{ requires2FA ? 'Vérification 2FA' : 'Connexion' }}
          </h1>
          <p class="text-corporate-500 dark:text-corporate-400">
            <template v-if="requires2FA && twoFactorMethod === 'email'">
              Un code de vérification a été envoyé à votre adresse email
            </template>
            <template v-else-if="requires2FA">
              Entrez le code de votre application d'authentification
            </template>
            <template v-else>
              Accédez à votre espace de gestion
            </template>
          </p>
        </div>

        <!-- Error Alert -->
        <div
          v-if="error"
          class="mb-6 p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800"
        >
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="text-sm text-error-700 dark:text-error-400">{{ error }}</p>
          </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-5">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-1.5">
              Adresse email
            </label>
            <input
              v-model="form.email"
              type="email"
              id="email"
              placeholder="vous@exemple.com"
              autocomplete="email"
              :disabled="requires2FA"
              class="w-full h-11 px-4 rounded-lg border border-corporate-300 bg-white text-corporate-900 placeholder:text-corporate-400 transition-all duration-150 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white dark:placeholder:text-corporate-500 disabled:bg-corporate-50 disabled:cursor-not-allowed dark:disabled:bg-corporate-800"
            />
          </div>

          <!-- Password -->
          <div v-if="!requires2FA">
            <label for="password" class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-1.5">
              Mot de passe
            </label>
            <div class="relative">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                id="password"
                placeholder="••••••••"
                autocomplete="current-password"
                class="w-full h-11 px-4 pr-11 rounded-lg border border-corporate-300 bg-white text-corporate-900 placeholder:text-corporate-400 transition-all duration-150 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white dark:placeholder:text-corporate-500"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-corporate-400 hover:text-corporate-600 dark:hover:text-corporate-300 transition-colors"
              >
                <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- 2FA Code -->
          <div v-if="requires2FA">
            <label for="code" class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-1.5">
              Code d'authentification
            </label>
            <input
              v-model="form.twoFactorCode"
              type="text"
              id="code"
              placeholder="000000"
              maxlength="6"
              autocomplete="one-time-code"
              class="w-full h-11 px-4 rounded-lg border border-corporate-300 bg-white text-corporate-900 text-center font-mono text-lg tracking-[0.5em] placeholder:text-corporate-400 placeholder:tracking-normal transition-all duration-150 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
            />
            <!-- Resend code button for email 2FA -->
            <div v-if="twoFactorMethod === 'email'" class="mt-2 text-center">
              <button
                type="button"
                @click="resend2FACode"
                :disabled="resendCooldown > 0 || resendLoading"
                class="text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span v-if="resendLoading">Envoi en cours...</span>
                <span v-else-if="resendCooldown > 0">Renvoyer le code dans {{ resendCooldown }}s</span>
                <span v-else>Renvoyer le code</span>
              </button>
            </div>
          </div>

          <!-- Remember & Forgot -->
          <div v-if="!requires2FA" class="flex items-center justify-between">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.rememberMe"
                type="checkbox"
                class="w-4 h-4 rounded border-corporate-300 text-accent-600 focus:ring-accent-500 dark:border-corporate-600 dark:bg-corporate-800"
              />
              <span class="text-sm text-corporate-600 dark:text-corporate-400">Se souvenir de moi</span>
            </label>
            <router-link
              to="/forgot-password"
              class="text-sm font-medium text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
            >
              Mot de passe oublié ?
            </router-link>
          </div>

          <!-- Submit -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full h-11 flex items-center justify-center gap-2 rounded-lg border-2 border-corporate-800 bg-transparent hover:bg-corporate-800 text-corporate-800 hover:text-white dark:border-0 dark:bg-[#1E3A5F] dark:hover:bg-[#152238] dark:text-white font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-corporate-400/50 dark:focus:ring-[#1E3A5F]/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-corporate-950"
          >
            <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            {{ requires2FA ? 'Vérifier' : 'Se connecter' }}
          </button>

          <!-- Cancel 2FA -->
          <button
            v-if="requires2FA"
            type="button"
            @click="cancel2FA"
            class="w-full text-sm text-corporate-500 hover:text-corporate-700 dark:text-corporate-400 dark:hover:text-corporate-200 transition-colors"
          >
            Utiliser un autre compte
          </button>
        </form>

        <!-- Register Link -->
        <p v-if="!requires2FA" class="mt-8 text-center text-sm text-corporate-500 dark:text-corporate-400">
          Pas encore de compte ?
          <router-link
            to="/register"
            class="font-medium text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
          >
            Créer un compte
          </router-link>
        </p>

        <!-- Footer -->
        <p class="mt-8 text-center text-xs text-corporate-400 dark:text-corporate-500">
          &copy; {{ new Date().getFullYear() }} Laumeris. Tous droits réservés.
        </p>
      </div>
    </div>

    <!-- Right Panel - Animated Partnership (Theme Adaptive) -->
    <div class="hidden lg:flex lg:w-1/2 bg-white dark:bg-corporate-950 items-center justify-center relative overflow-hidden">
      
      <!-- Animated Background Orbs -->
      <div class="absolute inset-0 overflow-hidden">
        <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-[#1E3A5F]/10 dark:bg-[#1E3A5F]/20 rounded-full blur-[120px] animate-pulse-slow" />
        <div class="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#C9B896]/5 dark:bg-[#C9B896]/10 rounded-full blur-[100px] animate-pulse-slower" />
      </div>

      <!-- Content Container -->
      <div class="relative z-10 flex flex-col items-center justify-center h-full px-12 py-16">
        
        <!-- Animated Logos -->
        <div class="flex items-center gap-6 mb-14 animate-fade-in-up" style="animation-delay: 0.2s;">
          <!-- Logo 1 -->
          <div class="relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-[#C9B896]/20 to-[#1E3A5F]/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div class="relative w-24 h-24 bg-corporate-100/50 dark:bg-white/[0.02] backdrop-blur-xl border border-corporate-200 dark:border-white/[0.08] rounded-2xl flex items-center justify-center transition-transform duration-500 hover:scale-105">
              <img src="/images/logo-icon.png" alt="Partner 1" class="h-14 w-auto" />
            </div>
          </div>

          <!-- Animated Connector -->
          <div class="flex flex-col items-center gap-1 animate-fade-in" style="animation-delay: 0.5s;">
            <div class="w-[1px] h-4 bg-gradient-to-b from-transparent to-[#C9B896]/40 dark:to-[#C9B896]/30" />
            <div class="w-3 h-3 border border-[#C9B896]/50 dark:border-[#C9B896]/40 rotate-45" />
            <div class="w-[1px] h-4 bg-gradient-to-t from-transparent to-[#C9B896]/40 dark:to-[#C9B896]/30" />
          </div>

          <!-- Logo 2 -->
          <div class="relative group">
            <div class="absolute -inset-1 bg-gradient-to-r from-[#1E3A5F]/20 to-[#C9B896]/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div class="relative w-24 h-24 bg-corporate-100/50 dark:bg-white/[0.02] backdrop-blur-xl border border-corporate-200 dark:border-white/[0.08] rounded-2xl flex items-center justify-center transition-transform duration-500 hover:scale-105">
              <img src="/images/logo-lmw.png" alt="Partner 2" class="h-16 w-auto" />
            </div>
          </div>
        </div>

        <!-- Title with reveal animation -->
        <div class="text-center mb-10 animate-fade-in-up" style="animation-delay: 0.4s;">
          <div class="inline-flex items-center gap-3 mb-6">
            <div class="w-8 h-[1px] bg-gradient-to-r from-transparent to-[#C9B896]/60 dark:to-[#C9B896]/50" />
            <span class="text-[11px] text-[#C9B896] dark:text-[#C9B896]/60 uppercase tracking-[0.4em] font-light">Partenariat</span>
            <div class="w-8 h-[1px] bg-gradient-to-l from-transparent to-[#C9B896]/60 dark:to-[#C9B896]/50" />
          </div>
          <h2 class="text-3xl font-extralight text-corporate-800 dark:text-white/90 tracking-wide mb-4">
            Plateforme de Gestion
          </h2>
          <p class="text-sm text-corporate-500 dark:text-white/40 max-w-sm leading-relaxed">
            Une alliance d'excellence au service de votre entreprise
          </p>
        </div>

        <!-- Animated Features -->
        <div class="flex gap-12 mb-14 animate-fade-in-up" style="animation-delay: 0.6s;">
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Sécurisé</p>
          </div>
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Performant</p>
          </div>
          <div class="group text-center cursor-default">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-corporate-100 dark:bg-white/[0.05] border border-corporate-200 dark:border-white/[0.08] flex items-center justify-center transition-all duration-300 group-hover:border-[#C9B896]/50 dark:group-hover:border-[#C9B896]/30 group-hover:shadow-[0_0_20px_rgba(201,184,150,0.15)] dark:group-hover:shadow-[0_0_20px_rgba(201,184,150,0.1)]">
              <svg class="w-5 h-5 text-corporate-400 dark:text-white/50 group-hover:text-[#C9B896] dark:group-hover:text-[#C9B896]/80 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <p class="text-[10px] text-corporate-400 dark:text-white/30 uppercase tracking-widest group-hover:text-corporate-600 dark:group-hover:text-white/50 transition-colors">Innovant</p>
          </div>
        </div>

        <!-- Bottom signature - Now in flow -->
        <div class="text-center animate-fade-in" style="animation-delay: 0.8s;">
          <div class="inline-flex items-center gap-2">
            <div class="w-1 h-1 rounded-full bg-[#C9B896]/60 dark:bg-[#C9B896]/40" />
            <p class="text-[10px] text-corporate-400 dark:text-white/20 uppercase tracking-[0.3em] font-light">Excellence & Confiance</p>
            <div class="w-1 h-1 rounded-full bg-[#C9B896]/60 dark:bg-[#C9B896]/40" />
          </div>
        </div>
      </div>

      <!-- Corner accents -->
      <div class="absolute top-0 left-0 w-32 h-32">
        <div class="absolute top-8 left-8 w-[1px] h-16 bg-gradient-to-b from-corporate-200 dark:from-white/10 to-transparent" />
        <div class="absolute top-8 left-8 w-16 h-[1px] bg-gradient-to-r from-corporate-200 dark:from-white/10 to-transparent" />
      </div>
      <div class="absolute bottom-0 right-0 w-32 h-32">
        <div class="absolute bottom-8 right-8 w-[1px] h-16 bg-gradient-to-t from-corporate-200 dark:from-white/10 to-transparent" />
        <div class="absolute bottom-8 right-8 w-16 h-[1px] bg-gradient-to-l from-corporate-200 dark:from-white/10 to-transparent" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const form = reactive({
  email: '',
  password: '',
  rememberMe: false,
  twoFactorCode: ''
})

const error = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const requires2FA = ref(false)
const tempToken = ref('')
const twoFactorMethod = ref<'email' | 'totp'>('email')
const resendCooldown = ref(0)
const resendLoading = ref(false)
let cooldownInterval: ReturnType<typeof setInterval> | null = null

const handleSubmit = async () => {
  error.value = ''
  isLoading.value = true

  try {
    if (requires2FA.value) {
      if (!form.twoFactorCode || form.twoFactorCode.length !== 6) {
        error.value = 'Veuillez entrer un code à 6 chiffres'
        isLoading.value = false
        return
      }

      const result = await authStore.verify2FA({
        tempToken: tempToken.value,
        code: form.twoFactorCode,
        method: twoFactorMethod.value
      })

      if (result.success) {
        toast.success('Connexion réussie', 'Bienvenue !')
        router.push('/dashboard')
      } else {
        error.value = result.error || 'Code invalide'
      }
    } else {
      if (!form.email) {
        error.value = 'L\'email est requis'
        isLoading.value = false
        return
      }
      if (!form.password) {
        error.value = 'Le mot de passe est requis'
        isLoading.value = false
        return
      }

      const result = await authStore.login({
        email: form.email,
        password: form.password,
        rememberMe: form.rememberMe
      })

      if (result.requires2FA) {
        requires2FA.value = true
        tempToken.value = result.tempToken || ''
        twoFactorMethod.value = result.twoFactorMethod || 'email'
        if (twoFactorMethod.value === 'email') {
          startResendCooldown()
        }
      } else if (result.success) {
        toast.success('Connexion réussie', 'Bienvenue !')
        router.push('/dashboard')
      } else {
        error.value = result.error || 'Identifiants incorrects'
      }
    }
  } catch (e: any) {
    error.value = e.message || 'Une erreur est survenue'
  } finally {
    isLoading.value = false
  }
}

const cancel2FA = () => {
  requires2FA.value = false
  tempToken.value = ''
  form.twoFactorCode = ''
  form.password = ''
  if (cooldownInterval) {
    clearInterval(cooldownInterval)
    cooldownInterval = null
  }
  resendCooldown.value = 0
}

const startResendCooldown = () => {
  resendCooldown.value = 60
  cooldownInterval = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      if (cooldownInterval) {
        clearInterval(cooldownInterval)
        cooldownInterval = null
      }
    }
  }, 1000)
}

const resend2FACode = async () => {
  if (resendCooldown.value > 0 || resendLoading.value) return
  
  resendLoading.value = true
  error.value = ''
  
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.laumeris.com/api'}/security/2fa/send-email-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tempToken: tempToken.value,
        action: 'login'
      })
    })
    
    const data = await response.json()
    if (data.success) {
      toast.success('Code renvoyé', 'Un nouveau code a été envoyé à votre email')
      startResendCooldown()
    } else {
      error.value = data.message || 'Erreur lors de l\'envoi du code'
    }
  } catch (e: any) {
    error.value = 'Erreur lors de l\'envoi du code'
  } finally {
    resendLoading.value = false
  }
}
</script>

<style scoped>
/* Fade in up animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Simple fade in */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slow pulse for background orbs */
@keyframes pulseSlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
}

@keyframes pulseSlower {
  0%, 100% {
    opacity: 0.2;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.15);
  }
}

.animate-fade-in-up {
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
}

.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-pulse-slow {
  animation: pulseSlow 8s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulseSlower 10s ease-in-out infinite;
  animation-delay: 2s;
}
</style>
